import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { processManifest } from './mapUtils';
import { GAME_MODES } from './mapMain';
import {
    DEFAULT_MODULE_DIMENSIONS,
    MODULE_DIMENSION_OVERRIDES
} from './mapConstants';

import { extractRulesLogic, inferEntityGroupId } from './mapLayoutService.js';

const REGEX_ITEM_ID_OR_LOOTDROP_PREFIX = /^(Id_Item_|Id_Lootdrop_)/;
const REGEX_RARITY_SUFFIX = /_(\d{4})$/;
const REGEX_UNDERSCORE = /_/g;

const getCacheBustUrl = (url) => `${url}?_cb=${Date.now()}`;

function cleanItemId(itemId) {
    if (!itemId || typeof itemId !== 'string') return 'Unknown Item';
    const cleaned = itemId.replace(REGEX_ITEM_ID_OR_LOOTDROP_PREFIX, '').replace(REGEX_RARITY_SUFFIX, '');
    return cleaned.replace(REGEX_UNDERSCORE, ' ');
}

function determineDungeonGradeKey(selectedMapType, gameMode) {
    if (!selectedMapType || !gameMode) return '0';
    let prefix = '10'; 
    const gameModeObject = Object.values(GAME_MODES).find(m => m.key === gameMode);
    if(gameModeObject) {
        switch (gameModeObject.key) {
            case GAME_MODES.ADVENTURE.key: prefix = '10'; break;
            case GAME_MODES.NORMAL.key: prefix = '20'; break;
            case GAME_MODES.HR.key: prefix = '30'; break;
        }
    }

    let suffix = '01'; 
    const mapIdLower = String(selectedMapType || '').toLowerCase();
    if (mapIdLower.includes('ruin') || mapIdLower.includes('goblin')) suffix = '21'; 
    else if (mapIdLower.includes('crypt') || mapIdLower.includes('ice cave')) suffix = '22'; 
    else if (mapIdLower.includes('inferno') || mapIdLower.includes('hell') || mapIdLower.includes('abyss')) suffix = '23'; 
    

    let baseGradeKey = prefix + suffix;
    if (baseGradeKey === "0000" || baseGradeKey === "0001" || baseGradeKey === "0021" ) baseGradeKey = "0"; 
    return baseGradeKey;
}

function findLootGroupEntryCaseInsensitive(dataObject, keyToFind) {
    if (!dataObject || typeof keyToFind !== 'string') { return undefined; }
    const lowerKeyToFind = keyToFind.toLowerCase();
    if (Object.prototype.hasOwnProperty.call(dataObject, keyToFind)) { return dataObject[keyToFind]; }
    for (const keyInObject in dataObject) {
        if (Object.prototype.hasOwnProperty.call(dataObject, keyInObject)) {
            if (keyInObject.toLowerCase() === lowerKeyToFind) { return dataObject[keyInObject]; }
        }
    }
    return undefined;
}

export function useMapData() {
    const [availableMapTypes, setAvailableMapTypes] = useState({});
    const [isManifestLoading, setIsManifestLoading] = useState(true);
    const [manifestError, setManifestError] = useState(null);
    const [selectedMapType, setSelectedMapType] = useState(() => {
        const stored = localStorage.getItem('selectedMapType');
        return stored || null;
    });
    const [focusedModuleName, setFocusedModuleName] = useState(() => localStorage.getItem('focusedModuleName') || null);
    const [gameMode, setGameMode] = useState(() => localStorage.getItem('gameMode') || GAME_MODES.NORMAL.key);

    const [rawModuleMetadata, setRawModuleMetadata] = useState(null);
    const [currentMapStructure, setCurrentMapStructure] = useState({});
    const [moduleDetailsForLegend, setModuleDetailsForLegend] = useState([]);
    const [currentMapData, setCurrentMapData] = useState({});

    const [isLoadingMapData, setIsLoadingMapData] = useState(false);
    const [mapDataError, setMapDataError] = useState(null);

    const [lootDataByGrade, setLootDataByGrade] = useState(new Map());
    const [isLoadingLootForGrade, setIsLoadingLootForGrade] = useState(new Map());
    const [lootErrorForGrade, setLootErrorForGrade] = useState(new Map());
    const lootDataPromisesRef = useRef(new Map());
    const [currentDungeonGradeKey, setCurrentDungeonGradeKey] = useState(null);
    const [currentLootData, setCurrentLootData] = useState(null);

    const [itemSearchTerm, setItemSearchTerm] = useState('');
    const [itemSearchResult, setItemSearchResult] = useState({
        foundItemNames: [], foundSources: new Set(),
        filteredModuleNames: null, searchPerformed: false, error: null
    });
    const [itemToSourcesMap, setItemToSourcesMap] = useState(new Map());
    const [sourceToModulesMap, setSourceToModulesMap] = useState(new Map());
    const [isBuildingIndex, setIsBuildingIndex] = useState(false);
    const [isSearchIndexReady, setIsSearchIndexReady] = useState(false);

    const buildIndexAnimationFrameRef = useRef(null);
    const currentFetchGeneration = useRef(0);

    useEffect(() => {
        let isMounted = true;
        setIsManifestLoading(true); setManifestError(null);
        axios.get(getCacheBustUrl('/ProcessedModules/map_manifest.json'))
            .then(async (response) => {
                if (!isMounted) return;
                const { processedData, firstValidMapType } = await processManifest(response.data);
                if (isMounted) {
                    setAvailableMapTypes(processedData);
                    const storedMapType = localStorage.getItem('selectedMapType');
                    const mapTypeToUse = processedData[storedMapType] ? storedMapType : firstValidMapType;

                    if (mapTypeToUse) {
                        setSelectedMapType(mapTypeToUse);
                    } else {
                        console.warn("[useMapData] No valid map type to select after manifest processing.");
                        setSelectedMapType(null); setFocusedModuleName(null);
                        setManifestError("No valid map types found or no default map type could be determined.");
                    }
                }
            })
            .catch(err => {
                console.error("[useMapData] Manifest load error:", err);
                if (isMounted) { setManifestError(`Failed to load map list: ${err.message}. Status: ${err.response?.status}`); }
            })
            .finally(() => {
                if (isMounted) {
                    setIsManifestLoading(false);
                }
            });
        return () => { isMounted = false; };
    }, []);

    const ensureLootDataIsLoaded = useCallback(async (gradeKey) => {
        if (!gradeKey) return null;
        if (lootDataByGrade.has(gradeKey)) return lootDataByGrade.get(gradeKey);
        if (lootDataPromisesRef.current.has(gradeKey)) return lootDataPromisesRef.current.get(gradeKey);

        setIsLoadingLootForGrade(prev => new Map(prev).set(gradeKey, true));
        setLootErrorForGrade(prev => { const newMap = new Map(prev); newMap.delete(gradeKey); return newMap; });

        const lootPath = `/ProcessedModules/grades_data/loot_data_${gradeKey}.json`;
        const promise = axios.get(getCacheBustUrl(lootPath))
            .then(response => {
                if (!response.data || typeof response.data !== 'object') throw new Error(`Invalid loot data structure for grade ${gradeKey}`);
                setLootDataByGrade(prev => new Map(prev).set(gradeKey, response.data));
                return response.data;
            })
            .catch(err => {
                setLootErrorForGrade(prev => new Map(prev).set(gradeKey, `Failed to load loot for grade ${gradeKey}: ${err.message}. Status: ${err.response?.status}. Popups may not show loot for this grade.`));
                setLootDataByGrade(prev => new Map(prev).set(gradeKey, {}));
                throw err;
            })
            .finally(() => {
                setIsLoadingLootForGrade(prev => new Map(prev).set(gradeKey, false));
                lootDataPromisesRef.current.delete(gradeKey);
            });
        lootDataPromisesRef.current.set(gradeKey, promise);
        return promise;
    }, [lootDataByGrade]);

    useEffect(() => {
        if (selectedMapType && gameMode && !isManifestLoading) {
            const gradeKey = determineDungeonGradeKey(selectedMapType, gameMode);
            setCurrentDungeonGradeKey(gradeKey);
            if (gradeKey) {
                ensureLootDataIsLoaded(gradeKey)
                    .then(data => { if (data) setCurrentLootData(data); else setCurrentLootData(null); })
                    .catch(() => { setCurrentLootData(null); });
            } else setCurrentLootData(null);
        } else {
            setCurrentDungeonGradeKey(null); setCurrentLootData(null);
        }
    }, [selectedMapType, gameMode, isManifestLoading, ensureLootDataIsLoaded]);

    useEffect(() => {
        let isMounted = true;
        const fetchId = currentFetchGeneration.current;

        if (!selectedMapType || isManifestLoading || Object.keys(availableMapTypes).length === 0 || !availableMapTypes[selectedMapType]) {
            setRawModuleMetadata(null); setCurrentMapData({}); setMapDataError(null); setIsLoadingMapData(false);
            return;
        }

        setIsLoadingMapData(true);
        setMapDataError(null);
        setRawModuleMetadata(null);
        setCurrentMapStructure({});
        setModuleDetailsForLegend([]);
        setCurrentMapData({});

        const mapInfo = availableMapTypes[selectedMapType];
        if (!mapInfo || !mapInfo.mapDataPath) {
            const errorMsg = `Manifest missing mapDataPath for: ${selectedMapType}`;
            console.error(`[useMapData gen:${fetchId}] ${errorMsg}`);
            if (isMounted) { setMapDataError(errorMsg); setIsLoadingMapData(false); }
            return;
        }

        axios.get(getCacheBustUrl(mapInfo.mapDataPath))
            .then(async metaResponse => {
                if (!isMounted || fetchId !== currentFetchGeneration.current) {
                    return;
                }
                if (!metaResponse.data || !Array.isArray(metaResponse.data)) {
                    const errorMsg = `Invalid data structure in ${mapInfo.mapDataPath}. Expected array. Received: ${JSON.stringify(metaResponse.data).substring(0,100)}`;
                    console.error(`[useMapData gen:${fetchId}] ${errorMsg}`);
                    if (isMounted) { setMapDataError(errorMsg); setRawModuleMetadata(null); setIsLoadingMapData(false); }
                    return;
                }

                const modulesMetadataWithImagePath = metaResponse.data.filter(moduleMeta => {
                    if (moduleMeta && moduleMeta.imagePath && typeof moduleMeta.imagePath === 'string') {
                        return true;
                    }
                    // console.warn(`[useMapData gen:${fetchId}] Module ${moduleMeta?.moduleName || 'Unknown'} in ${selectedMapType} meta file lacks a valid imagePath. It will be excluded.`);
                    return false;
                });

                if (modulesMetadataWithImagePath.length === 0) {
                    // console.warn(`[useMapData gen:${fetchId}] No modules with valid imagePaths found in ${mapInfo.mapDataPath} for ${selectedMapType}.`);
                    if (isMounted) {
                        setRawModuleMetadata([]);
                    }
                    return;
                }

                const textureDataPromises = modulesMetadataWithImagePath.map(moduleMeta => {
                    if (moduleMeta && moduleMeta.imagePath && typeof moduleMeta.imagePath === 'string') {
                        const textureJsonPath = moduleMeta.imagePath.replace(/\.(png|jpg|jpeg|gif|webp)$/i, '.json');
                        if (textureJsonPath === moduleMeta.imagePath) {
                            return Promise.resolve({ moduleName: moduleMeta.moduleName, status: 'skipped', reason: 'No valid image extension in imagePath to replace for texture JSON path' });
                        }
                        return axios.get(getCacheBustUrl(textureJsonPath))
                            .then(textureResponse => ({
                                moduleName: moduleMeta.moduleName,
                                status: 'fulfilled',
                                data: textureResponse.data
                            }))
                            .catch(err => ({
                                moduleName: moduleMeta.moduleName,
                                status: 'rejected',
                                reasonInfo: { message: `Failed to load texture JSON for ${moduleMeta.moduleName} from ${textureJsonPath}: ${err.message}. Status: ${err.response?.status}`, error: err }
                            }));
                    } else {
                        return Promise.resolve({ moduleName: moduleMeta?.moduleName, status: 'skipped', reason: 'No imagePath provided (should have been filtered)' });
                    }
                });

                const textureResults = await Promise.allSettled(textureDataPromises);

                if (!isMounted || fetchId !== currentFetchGeneration.current) {
                    return;
                }

                const finalProcessedModules = modulesMetadataWithImagePath.map((moduleMeta) => {
                    const newModuleMeta = { ...moduleMeta };
                    const textureResultContainer = textureResults.find(tr =>
                        (tr.status === 'fulfilled' && tr.value?.moduleName === moduleMeta.moduleName) ||
                        (tr.status === 'rejected' && tr.reason?.moduleName === moduleMeta.moduleName)
                    );

                    newModuleMeta.originalMetaDimensions = moduleMeta.dimensions ? { ...moduleMeta.dimensions } : null;
                    newModuleMeta.textureActualDimensions = null;

                    if (textureResultContainer?.status === 'fulfilled' && textureResultContainer.value.status === 'fulfilled') {
                        const textureData = textureResultContainer.value.data;
                        if (Array.isArray(textureData) && textureData.length > 0 &&
                            typeof textureData[0].SizeX === 'number' && typeof textureData[0].SizeY === 'number') {
                            newModuleMeta.textureActualDimensions = {
                                width: textureData[0].SizeX,
                                height: textureData[0].SizeY
                            };
                        } else {
                             // console.warn(`[useMapData gen:${fetchId}] Module ${moduleMeta.moduleName}: Texture JSON loaded but invalid format or missing SizeX/SizeY. Data:`, textureData, `Path: ${moduleMeta.imagePath?.replace(/\.(png|jpg|jpeg|gif|webp)$/i, '.json')}. Falling back.`);
                        }
                    } else if (textureResultContainer?.status === 'rejected') {
                         // console.warn(`[useMapData gen:${fetchId}] Module ${moduleMeta.moduleName}: ${textureResultContainer.reason.reasonInfo.message}. Falling back.`);
                    }
                    return newModuleMeta;
                });
                if (isMounted) {
                    setRawModuleMetadata(finalProcessedModules);
                }
            })
            .catch(err => {
                if (!isMounted || fetchId !== currentFetchGeneration.current) {
                     return;
                }
                const errorMsg = `Failed to load map metadata for ${selectedMapType} from ${mapInfo.mapDataPath}: ${err.message}. Status: ${err.response?.status}`;
                console.error(`[useMapData gen:${fetchId}] ${errorMsg}`, err);
                if (isMounted) { setMapDataError(errorMsg); setRawModuleMetadata(null); setIsLoadingMapData(false); }
            });
        return () => { isMounted = false; };
    }, [selectedMapType, availableMapTypes, isManifestLoading]);


    useEffect(() => {
        const fetchId = currentFetchGeneration.current;
        if (!rawModuleMetadata) {
            setCurrentMapStructure({}); setModuleDetailsForLegend([]);
            if(rawModuleMetadata === null || (Array.isArray(rawModuleMetadata) && rawModuleMetadata.length === 0)) {
                 setIsLoadingMapData(false);
            }
            return;
        }
        if (Array.isArray(rawModuleMetadata) && rawModuleMetadata.length === 0) {
            setCurrentMapStructure({});
            setModuleDetailsForLegend([]);
            setIsLoadingMapData(false);
            return;
        }

        try {
            const newMapStructure = {}; const tempLegendDetails = [];
            const usedLegendKeys = new Set();

            rawModuleMetadata.forEach(moduleMeta => {
                if (!moduleMeta || typeof moduleMeta.moduleName !== 'string' ||
                    typeof moduleMeta.actorDataPath !== 'string' ||
                    !moduleMeta.imagePath || typeof moduleMeta.imagePath !== 'string') {
                     // console.warn(`[useMapData gen:${fetchId}] Skipping invalid module meta (or missing imagePath) in rawModuleMetadata processing:`, moduleMeta);
                     return;
                }

                let baseDisplayStringForLegend;
                if (moduleMeta.Module_LocalizedString && moduleMeta.Module_LocalizedString !== "N/A") {
                    baseDisplayStringForLegend = moduleMeta.Module_LocalizedString;
                } else {
                    baseDisplayStringForLegend = moduleMeta.moduleName;
                }
                let uniqueLegendDisplayString = baseDisplayStringForLegend;
                let counter = 1;
                while (usedLegendKeys.has(uniqueLegendDisplayString)) {
                    counter++;
                    uniqueLegendDisplayString = `${baseDisplayStringForLegend} (${counter})`;
                }
                usedLegendKeys.add(uniqueLegendDisplayString);

                tempLegendDetails.push({
                    moduleName: moduleMeta.moduleName,
                    imagePath: moduleMeta.imagePath,
                    Module_LocalizedString: uniqueLegendDisplayString
                });

                const tileVisualDimensions = MODULE_DIMENSION_OVERRIDES[moduleMeta.moduleName] ||
                                             (moduleMeta.textureActualDimensions && typeof moduleMeta.textureActualDimensions.width === 'number' && typeof moduleMeta.textureActualDimensions.height === 'number' ? moduleMeta.textureActualDimensions : null) ||
                                             (moduleMeta.originalMetaDimensions && typeof moduleMeta.originalMetaDimensions.width === 'number' && typeof moduleMeta.originalMetaDimensions.height === 'number' ? moduleMeta.originalMetaDimensions : null) ||
                                             DEFAULT_MODULE_DIMENSIONS;

                newMapStructure[moduleMeta.moduleName] = {
                    imagePath: moduleMeta.imagePath,
                    dimensions: tileVisualDimensions,
                    originalMetaDimensions: moduleMeta.originalMetaDimensions,
                    moduleTextureActualDimensions: moduleMeta.textureActualDimensions,
                    Module_LocalizedString: moduleMeta.Module_LocalizedString,
                    actorDataPath: moduleMeta.actorDataPath
                };
            });
            setCurrentMapStructure(newMapStructure);
            setModuleDetailsForLegend(tempLegendDetails.sort((a, b) => {
                const nameA = a.Module_LocalizedString;
                const nameB = b.Module_LocalizedString;
                return nameA.localeCompare(nameB);
            }));
            if (Object.keys(newMapStructure).length === 0) {
                 setIsLoadingMapData(false);
            }
        } catch (error) {
            console.error(`[useMapData gen:${fetchId}] Error processing rawModuleMetadata into structure:`, error);
            setCurrentMapStructure({}); setModuleDetailsForLegend([]);
            setMapDataError("Failed to process map structure from metadata.");
            setIsLoadingMapData(false);
        }
    }, [rawModuleMetadata]);

    useEffect(() => {
        const fetchId = currentFetchGeneration.current;

        if (Object.keys(currentMapStructure).length === 0) {
            setCurrentMapData({});
            if (rawModuleMetadata === null || (Array.isArray(rawModuleMetadata) && rawModuleMetadata.length === 0)){
                 // Already handled by previous effect setting isLoadingMapData to false
            }
            return;
        }

        const initialModulesData = {};
        Object.keys(currentMapStructure).forEach(moduleName => {
            initialModulesData[moduleName] = {
                ...currentMapStructure[moduleName],
                data: [],
                isLoadingActors: true,
                errorLoadingActors: null
            };
        });
        setCurrentMapData(initialModulesData);

        const promises = Object.entries(currentMapStructure).map(([moduleName, moduleStruct]) => {
            return axios.get(getCacheBustUrl(moduleStruct.actorDataPath))
                .then(response => {
                    if (fetchId !== currentFetchGeneration.current) {
                        return null;
                    }
                    const actors = response.data;
                    if (actors && (actors.N_Data !== undefined || actors.HR_Data !== undefined)) {
                        const useHr = gameMode === GAME_MODES.HR.key; // Simplified this condition, assuming HR_Data for HR, N_Data otherwise
                        const actorDataForMode = useHr ? (actors.HR_Data || []) : (actors.N_Data || []);
                        const processedActorData = actorDataForMode.map(actor => ({
                            ...actor,
                            entityGroupId: actor.entityGroupId || inferEntityGroupId(actor.object_name)
                        }));
                        return { moduleName, data: processedActorData, error: null };
                    } else {
                        throw new Error(`Invalid actor data structure for ${moduleName}`);
                    }
                })
                .catch(err => {
                    if (fetchId !== currentFetchGeneration.current) {
                        return null;
                    }
                    console.error(`[useMapData gen:${fetchId}] Failed to load actor data for ${moduleName} (${moduleStruct.actorDataPath}): ${err.message}. Status: ${err.response?.status}`);
                    return { moduleName, data: [], error: err.message };
                });
        });

        Promise.allSettled(promises).then((results) => {
            if (fetchId !== currentFetchGeneration.current) {
                return;
            }

            setCurrentMapData(prevMapData => {
                const newMapData = { ...prevMapData };
                results.forEach(result => {
                    if (result.status === 'fulfilled' && result.value) {
                        const { moduleName, data, error } = result.value;
                        if (newMapData[moduleName]) {
                            newMapData[moduleName] = {
                                ...newMapData[moduleName],
                                data: data,
                                isLoadingActors: false,
                                errorLoadingActors: error
                            };
                        }
                    } else if (result.status === 'rejected') {
                        console.error(`[useMapData gen:${fetchId}] Unhandled promise rejection in actor fetch:`, result.reason);
                    }
                });
                return newMapData;
            });
            setIsLoadingMapData(false);
        });
    }, [currentMapStructure, gameMode]);

    useEffect(() => {
        if (!isLoadingMapData) {
            if (Object.keys(currentMapData).length > 0) {
                if (focusedModuleName && !currentMapData[focusedModuleName]) {
                    setFocusedModuleName(null);
                }
            } else if (focusedModuleName) {
                setFocusedModuleName(null);
            }
        }
    }, [focusedModuleName, currentMapData, isLoadingMapData]);

    useEffect(() => {
        if (buildIndexAnimationFrameRef.current) { cancelAnimationFrame(buildIndexAnimationFrameRef.current); buildIndexAnimationFrameRef.current = null; }

        const isMapDataReadyForIndex = !isLoadingMapData &&
                                     currentMapData && Object.keys(currentMapData).length > 0 &&
                                     Object.values(currentMapData).every(mod => mod && !mod.isLoadingActors && mod.data !== undefined);

        if (!isMapDataReadyForIndex || currentLootData === null) {
            setItemToSourcesMap(new Map()); setSourceToModulesMap(new Map());
            setIsSearchIndexReady(false); setIsBuildingIndex(false); return;
        }

        if (typeof currentLootData === 'object' && Object.keys(currentLootData).length === 0 && currentLootData.constructor === Object) {
             setItemToSourcesMap(new Map()); setSourceToModulesMap(new Map());
             setIsSearchIndexReady(true); setIsBuildingIndex(false); return;
        }

        setIsBuildingIndex(true); setIsSearchIndexReady(false);
        const itemsToProcessForIndex = [];
        Object.entries(currentMapData).forEach(([moduleName, moduleContent]) => {
            if (moduleContent?.data && Array.isArray(moduleContent.data) && !moduleContent.isLoadingActors) {
                moduleContent.data.forEach(actor => {
                    if (actor?.object_name) {
                        itemsToProcessForIndex.push({
                            moduleName, object_name: actor.object_name,
                            spawn_chances_by_grade: actor.spawn_chances_by_grade,
                            allowed_pools: actor.allowed_pools, entityGroupId: actor.entityGroupId,
                            LocalizedString: actor.LocalizedString
                        });
                    }
                });
            }
        });

        if (itemsToProcessForIndex.length === 0) {
            setIsBuildingIndex(false); setIsSearchIndexReady(true);
            setItemToSourcesMap(new Map()); setSourceToModulesMap(new Map()); return;
        }

        let currentIndex = 0; const tempNewSrcToModMap = new Map(); const tempNewItemToSrcMap = new Map();
        const BATCH_SIZE = 100;

        function processIndexBatch() {
            let processedInThisBatch = 0;
            while (currentIndex < itemsToProcessForIndex.length && processedInThisBatch < BATCH_SIZE) {
                const actorAsSource = itemsToProcessForIndex[currentIndex];
                const srcName = actorAsSource.object_name;
                if (!tempNewSrcToModMap.has(srcName)) tempNewSrcToModMap.set(srcName, new Set());
                tempNewSrcToModMap.get(srcName).add(actorAsSource.moduleName);

                let selectedSpawnsForLuckRules = null;
                const entityGroupIdForRulesAndLoot = actorAsSource.entityGroupId;
                const spawn_chances_by_grade_for_actor = actorAsSource.spawn_chances_by_grade;

                if (spawn_chances_by_grade_for_actor && typeof spawn_chances_by_grade_for_actor === 'object' && Object.keys(spawn_chances_by_grade_for_actor).length > 0) {
                    const availableKeys = Object.keys(spawn_chances_by_grade_for_actor).sort();
                    let finalSelectedGradeKeyForActorSpawnRules = currentDungeonGradeKey;

                    if (!spawn_chances_by_grade_for_actor[finalSelectedGradeKeyForActorSpawnRules]) {
                        const pStr = String(currentDungeonGradeKey).substring(0,2);
                        const firstChar = String(currentDungeonGradeKey).charAt(0);
                        const fullPKeys = availableKeys.filter(k => k.startsWith(pStr));
                        const firstCharKeys = availableKeys.filter(k => k.startsWith(firstChar));

                        if (currentDungeonGradeKey === "0" && spawn_chances_by_grade_for_actor['0']) { finalSelectedGradeKeyForActorSpawnRules = '0'; }
                        else if (fullPKeys.length > 0) { finalSelectedGradeKeyForActorSpawnRules = fullPKeys[0]; }
                        else if (firstCharKeys.length > 0) { finalSelectedGradeKeyForActorSpawnRules = firstCharKeys[0]; }
                        else if (spawn_chances_by_grade_for_actor['0']) { finalSelectedGradeKeyForActorSpawnRules = '0'; }
                        else if (availableKeys.length > 0) { finalSelectedGradeKeyForActorSpawnRules = availableKeys[0]; }
                    }

                    const primaryGradeData = spawn_chances_by_grade_for_actor[finalSelectedGradeKeyForActorSpawnRules];
                    let potentialSpawns = extractRulesLogic(primaryGradeData, entityGroupIdForRulesAndLoot);

                    let primarySpawnIsEmptyObject = potentialSpawns && typeof potentialSpawns === 'object' && Object.keys(potentialSpawns).length === 0;
                    let primaryHasGradesAllowed = potentialSpawns && Array.isArray(potentialSpawns.grades_allowed);

                    if (!primaryHasGradesAllowed && primarySpawnIsEmptyObject &&
                        finalSelectedGradeKeyForActorSpawnRules !== '0' && spawn_chances_by_grade_for_actor['0']) {

                        let fallbackGradeData = spawn_chances_by_grade_for_actor['0'];
                        let fallbackSpawns = extractRulesLogic(fallbackGradeData, entityGroupIdForRulesAndLoot);

                        if (fallbackSpawns && Array.isArray(fallbackSpawns.grades_allowed)) { potentialSpawns = fallbackSpawns; }
                        else if (fallbackSpawns && Object.keys(fallbackSpawns).length > 0 && primarySpawnIsEmptyObject) { potentialSpawns = fallbackSpawns; }
                    }
                    selectedSpawnsForLuckRules = potentialSpawns;
                    if (selectedSpawnsForLuckRules === null && primaryGradeData && typeof primaryGradeData === 'object' && Object.keys(primaryGradeData).length === 0) {
                        selectedSpawnsForLuckRules = primaryGradeData;
                    }
                }

                const allowedPoolsFromActor = actorAsSource.allowed_pools;
                if (allowedPoolsFromActor && Array.isArray(allowedPoolsFromActor) && allowedPoolsFromActor.length > 0 && currentLootData) {
                    allowedPoolsFromActor.forEach(allowedPoolId_originalCase => {
                        const lootGroupEntry = findLootGroupEntryCaseInsensitive(currentLootData, allowedPoolId_originalCase);
                        if (!lootGroupEntry || !lootGroupEntry.gradeSpecificLoot || !Array.isArray(lootGroupEntry.gradeSpecificLoot.dropChecks) || lootGroupEntry.gradeSpecificLoot.dropChecks.length === 0) { return; }

                        const dropChecksForThisPool = lootGroupEntry.gradeSpecificLoot.dropChecks;
                        dropChecksForThisPool.forEach((checkData) => {
                            let currentCheckLuckConfig = null;
                            let currentCheckExplicitlyAllowedLuckGrades = new Set();
                            let currentCheckApplyLuckFilter = false;

                            if (selectedSpawnsForLuckRules && typeof selectedSpawnsForLuckRules === 'object') {
                                if (checkData.lootDropId && selectedSpawnsForLuckRules[checkData.lootDropId]?.grades_allowed) {
                                    currentCheckLuckConfig = selectedSpawnsForLuckRules[checkData.lootDropId];
                                } else if (selectedSpawnsForLuckRules[allowedPoolId_originalCase]?.grades_allowed) {
                                    currentCheckLuckConfig = selectedSpawnsForLuckRules[allowedPoolId_originalCase];
                                } else {
                                    const caseInsensitiveSpawnChanceKey = Object.keys(selectedSpawnsForLuckRules).find(k => k.toLowerCase() === allowedPoolId_originalCase.toLowerCase());
                                    if (caseInsensitiveSpawnChanceKey && selectedSpawnsForLuckRules[caseInsensitiveSpawnChanceKey]?.grades_allowed) {
                                        currentCheckLuckConfig = selectedSpawnsForLuckRules[caseInsensitiveSpawnChanceKey];
                                    } else if (Array.isArray(selectedSpawnsForLuckRules.grades_allowed) && typeof selectedSpawnsForLuckRules.percentage === 'number') {
                                        currentCheckLuckConfig = selectedSpawnsForLuckRules;
                                    }
                                }
                            }

                            if (currentCheckLuckConfig && Array.isArray(currentCheckLuckConfig.grades_allowed)) {
                                if (currentCheckLuckConfig.grades_allowed.length > 0) {
                                    currentCheckLuckConfig.grades_allowed.forEach(ga => {
                                        if (typeof ga.LuckGrade === 'number' && typeof ga.DropRate === 'number' && ga.DropRate > 0) {
                                            currentCheckExplicitlyAllowedLuckGrades.add(ga.LuckGrade);
                                        }
                                    });
                                    currentCheckApplyLuckFilter = true;
                                }
                            }

                            if (!checkData.possibleDrops || !Array.isArray(checkData.possibleDrops) || checkData.possibleDrops.length === 0) { return; }

                            let dropsToIndex = checkData.possibleDrops;
                            if (currentCheckApplyLuckFilter) {
                                dropsToIndex = checkData.possibleDrops.filter(drop =>
                                    typeof drop.luckGrade !== 'number' || currentCheckExplicitlyAllowedLuckGrades.has(drop.luckGrade)
                                );
                            }

                            dropsToIndex.forEach(drop => {
                                if (drop.itemId) {
                                    const namesToAdd = new Set();

                                
                                    let nameSourceForCleaning;
                                    if (drop.itemId.endsWith('_8001') && drop.artifactName) {
                                        nameSourceForCleaning = drop.artifactName;
                                    } else {
                                        nameSourceForCleaning = drop.itemName || drop.itemId;
                                    }
                                    namesToAdd.add(cleanItemId(nameSourceForCleaning));

                                   
                                    if (drop.itemId) {
                                        namesToAdd.add(cleanItemId(drop.itemId));
                                    }
                                    if (drop.itemName) {
                                        namesToAdd.add(cleanItemId(drop.itemName));
                                    }
                                    
                                    if (drop.artifactName) {
                                        namesToAdd.add(cleanItemId(drop.artifactName));
                                    }

                                    namesToAdd.forEach(nameKey => {
                                        if (nameKey && nameKey !== 'Unknown Item') {
                                            if (!tempNewItemToSrcMap.has(nameKey)) {
                                                tempNewItemToSrcMap.set(nameKey, new Set());
                                            }
                                            tempNewItemToSrcMap.get(nameKey).add(srcName);
                                        }
                                    });
                                }
                            });
                        });
                    });
                }
                currentIndex++; processedInThisBatch++;
            }

            if (currentIndex >= itemsToProcessForIndex.length) {
                setSourceToModulesMap(tempNewSrcToModMap);
                setItemToSourcesMap(tempNewItemToSrcMap);
                setIsSearchIndexReady(true);
                setIsBuildingIndex(false);
                buildIndexAnimationFrameRef.current = null;
                return;
            }
            buildIndexAnimationFrameRef.current = requestAnimationFrame(processIndexBatch);
        }
        buildIndexAnimationFrameRef.current = requestAnimationFrame(processIndexBatch);

        return () => { if (buildIndexAnimationFrameRef.current) { cancelAnimationFrame(buildIndexAnimationFrameRef.current); buildIndexAnimationFrameRef.current = null; }};
    }, [currentMapData, currentLootData, isLoadingMapData, currentDungeonGradeKey]);

    const searchItems = useCallback((term) => {
        const cleanedTerm = term.trim().toLowerCase();
        setItemSearchTerm(term);
        if (!cleanedTerm) { setItemSearchResult({ foundItemNames: [], foundSources: new Set(), filteredModuleNames: null, searchPerformed: false, error: null }); return; }
        if (isBuildingIndex) { setItemSearchResult(prev => ({ ...prev, searchPerformed: true, error: "Search index building..." })); return; }
        if (!isSearchIndexReady || currentLootData === null) {
             setItemSearchResult(prev => ({ ...prev, searchPerformed: true, error: "Search index not ready (loot data for current grade might be pending or failed to load)." })); return;
        }
        const baseErrorState = { searchPerformed: true, foundItemNames: [], foundSources: new Set(), filteredModuleNames: new Set() };

        if (itemToSourcesMap.size === 0) {
            if (currentLootData && typeof currentLootData === 'object' && Object.keys(currentLootData).length > 0) {
                 setItemSearchResult({ ...baseErrorState, error: "No items indexed for this map/mode/grade (check loot table structure or if current map has lootable entities that drop items)." });
            } else {
                 setItemSearchResult({ ...baseErrorState, error: "Loot data for current grade unavailable or empty, cannot search items." });
            }
            return;
        }

        const matchedItemNames = [];
        for (const itemName of itemToSourcesMap.keys()) {
            if (itemName.toLowerCase().includes(cleanedTerm)) {
                matchedItemNames.push(itemName);
            }
        }
        matchedItemNames.sort();

        if (matchedItemNames.length === 0) {
            setItemSearchResult({ ...baseErrorState, error: `No items found matching "${term}".` });
            return;
        }

        const sourcesForFoundItems = new Set();
        matchedItemNames.forEach(itemName => {
            const sources = itemToSourcesMap.get(itemName);
            if (sources) sources.forEach(src => sourcesForFoundItems.add(src));
        });

        if (sourcesForFoundItems.size === 0) {
            setItemSearchResult({ ...baseErrorState, foundItemNames: matchedItemNames, error: `Found items matching "${term}", but no entities on this map/mode/grade drop them (or sources not on modules).` });
            return;
        }

        const modulesWithTheseSources = new Set();
        sourcesForFoundItems.forEach(srcName => {
            const modules = sourceToModulesMap.get(srcName);
            if (modules) modules.forEach(modName => modulesWithTheseSources.add(modName));
        });

        setItemSearchResult({
            foundItemNames: matchedItemNames,
            foundSources: sourcesForFoundItems,
            filteredModuleNames: modulesWithTheseSources.size > 0 ? modulesWithTheseSources : new Set(),
            searchPerformed: true,
            error: modulesWithTheseSources.size === 0 ? `Items found, but their sources are not on the current map modules.` : null
        });
    }, [itemToSourcesMap, sourceToModulesMap, isBuildingIndex, isSearchIndexReady, currentLootData]);

    useEffect(() => { if (selectedMapType !== null) localStorage.setItem('selectedMapType', selectedMapType); else localStorage.removeItem('selectedMapType'); }, [selectedMapType]);
    useEffect(() => { if (focusedModuleName !== null) localStorage.setItem('focusedModuleName', focusedModuleName); else localStorage.removeItem('focusedModuleName'); }, [focusedModuleName]);
    useEffect(() => { localStorage.setItem('gameMode', gameMode); }, [gameMode]);

    const clearItemSearch = useCallback(() => {
        setItemSearchTerm('');
        setItemSearchResult({ foundItemNames: [], foundSources: new Set(), filteredModuleNames: null, searchPerformed: false, error: null });
    }, []);

    const handleSetSelectedMapType = useCallback((mapType) => {
        currentFetchGeneration.current += 1;
        setSelectedMapType(mapType);
        setFocusedModuleName(null);
        clearItemSearch();
        setRawModuleMetadata(null);
        setCurrentMapStructure({});
        setCurrentMapData({});
        setModuleDetailsForLegend([]);
        setIsSearchIndexReady(false);
        setIsBuildingIndex(false);
        if (buildIndexAnimationFrameRef.current) { cancelAnimationFrame(buildIndexAnimationFrameRef.current); buildIndexAnimationFrameRef.current = null; }
    }, [clearItemSearch, setSelectedMapType, setFocusedModuleName]);

    const handleSetGameMode = useCallback((newMode) => {
        if (Object.values(GAME_MODES).some(mode => mode.key === newMode)) {
            currentFetchGeneration.current += 1;
            setGameMode(newMode);
            clearItemSearch();
            setIsSearchIndexReady(false);
            setIsBuildingIndex(false);
            if (buildIndexAnimationFrameRef.current) { cancelAnimationFrame(buildIndexAnimationFrameRef.current); buildIndexAnimationFrameRef.current = null; }
        }
    }, [clearItemSearch, setGameMode]);

    const isCurrentGradeLootLoading = currentDungeonGradeKey ? (isLoadingLootForGrade.get(currentDungeonGradeKey) || false) : false;
    const currentGradeLootError = currentDungeonGradeKey ? (lootErrorForGrade.get(currentDungeonGradeKey) || null) : null;

    return {
        availableMapTypes, isManifestLoading, manifestError,
        selectedMapType, focusedModuleName, setFocusedModuleName,
        gameMode, setGameMode: handleSetGameMode,
        currentMapData,
        moduleDetailsForLegend,
        isLoadingMapData,
        mapDataError,
        lootData: currentLootData,
        isLoadingLoot: isCurrentGradeLootLoading,
        lootError: currentGradeLootError,
        ensureLootDataIsLoaded,
        lootDataByGrade,
        itemSearchTerm, setItemSearchTerm, itemSearchResult, searchItems, clearItemSearch,
        isBuildingIndex, isSearchIndexReady,
        setSelectedMapType: handleSetSelectedMapType,
        currentDungeonGradeKey,
    };
}